import { Plugins } from "@fancyapps/ui/src/Fancybox/plugins/index"

import "@fancyapps/ui/dist/fancybox.css"

import { FileActions, fileModule } from "@/store/modules/file.module"
import { bindFancyBox } from "@/utils/fancyboxHelper"
import { attachSlideStreamRecovery, getSlideFileMetadataId } from "@/utils/videoStreamRecovery"

const zoomToolbarOptions = {
  display: ["counter", "zoomIn", "zoomOut", "fullscreen", "thumbs", "close"],
  items: {
    ...Plugins.Toolbar.defaults.items,
    zoomIn: {
      type: "button",
      class: "fancybox__button--zoom fancybox__button--zoom-in",
      label: "ZOOM_IN",
      html: '<svg viewBox="0 0 24 24"><circle cx="10" cy="10" r="7"/><path d="M16 16 L21 21"/><path d="M10 7v6M7 10h6"/></svg>',
      click: function (this: any, event: MouseEvent) {
        event.preventDefault()
        const panzoom = this.fancybox.getSlide().Panzoom
        if (panzoom) panzoom.zoomIn()
      }
    },
    zoomOut: {
      type: "button",
      class: "fancybox__button--zoom fancybox__button--zoom-out",
      label: "ZOOM_OUT",
      html: '<svg viewBox="0 0 24 24"><circle cx="10" cy="10" r="7"/><path d="M16 16 L21 21"/><path d="M7 10h6"/></svg>',
      click: function (this: any, event: MouseEvent) {
        event.preventDefault()
        const panzoom = this.fancybox.getSlide().Panzoom
        if (panzoom) panzoom.zoomOut()
      }
    }
  }
}

const zoomImageOptions = {
  wheel: "zoom",
  Panzoom: {
    wheelFactor: 10,
    maxScale: function (this: any) {
      const naturalRatio =
        this.content?.origWidth > 0 && this.content?.fitWidth > 0
          ? this.content.origWidth / this.content.fitWidth
          : 4
      return Math.max(naturalRatio, 4)
    }
  }
}

let Fancybox = bindFancyBox({
  id: "[data-fancybox]",
  options: {
    closeButton: "outside",
    type: "image",
    click: "close",
    Image: zoomImageOptions,
    Toolbar: zoomToolbarOptions,
    on: {
      "Carousel.change": () => {
        const videos = document.querySelectorAll("video")
        videos.forEach(video => {
          video.pause()
        })
      }
    }
  }
})

const previewBlobUrls = {}
const fullSizeBlobUrls = {}

function shouldLoadFullSizeImage(slide) {
  return (
    (slide.$trigger?.dataset?.loadFullSize === "true" || slide.loadFullSize === true) &&
    (!slide.type || slide.type === "image")
  )
}

async function setPreviewContent(slide) {
  const fileMetadataId = getSlideFileMetadataId(slide)

  if (!slide.previewBlob || !fileMetadataId) {
    return slide
  }

  if (previewBlobUrls[fileMetadataId]) {
    slide.src = previewBlobUrls[fileMetadataId]
    slide.previewLoaded = true

    return slide
  }

  const blob = await slide.previewBlob({
    fileMetadataId
  })

  if (!blob) {
    return slide
  }

  const blobUrl = URL.createObjectURL(blob)
  previewBlobUrls[fileMetadataId] = blobUrl
  slide.src = blobUrl
  slide.previewLoaded = true

  return slide
}

async function setBlobContent(slide) {
  if (slide.type === "html5video") {
    slide.loaded = true
    return slide
  }

  if (previewBlobUrls[slide.fileMetadataId]) {
    slide.src = previewBlobUrls[slide.fileMetadataId]
    slide.loaded = true
    return slide
  }

  if (slide.$el) {
    slide.$el.classList.add("fancybox-loading")
  }

  const blob = await slide.blob({
    fileMetadataId: slide.fileMetadataId
  })

  const blobUrl = URL.createObjectURL(blob)
  previewBlobUrls[slide.fileMetadataId] = blobUrl
  slide.src = blobUrl
  slide.loaded = true

  if (slide.$el) {
    slide.$el.classList.remove("fancybox-loading")
  }

  return slide
}

async function replacePreviewWithFullSize(slide) {
  const fileMetadataId = getSlideFileMetadataId(slide)

  if (!fileMetadataId || slide.fullSizeLoading || slide.fullSizeLoaded) {
    return
  }

  slide.fileMetadataId = fileMetadataId

  if (fullSizeBlobUrls[fileMetadataId]) {
    slide.src = fullSizeBlobUrls[fileMetadataId]
    slide.fullSizeLoaded = true

    if (slide.$image && slide.$image.src !== slide.src) {
      slide.$image.src = slide.src
    }

    return
  }

  slide.fullSizeLoading = true

  try {
    const blob = await fileModule[FileActions.ATTACHMENT_VIEW]({
      fileMetadataId,
      fullSize: true
    })

    if (!blob) {
      return
    }

    const blobUrl = URL.createObjectURL(blob)
    fullSizeBlobUrls[fileMetadataId] = blobUrl
    slide.src = blobUrl
    slide.fullSizeLoaded = true

    if (slide.$image) {
      slide.$image.src = blobUrl
    }
  } finally {
    slide.fullSizeLoading = false
  }
}

class CustomImage extends Plugins.Image {
  async setContent(slide) {
    if (shouldLoadFullSizeImage(slide)) {
      if (!slide.src && !slide.previewLoaded) {
        slide = await setPreviewContent(slide)
      }

      if (!slide.$el) return

      super.setContent(slide)
      void replacePreviewWithFullSize(slide)
      return
    }

    if (slide?.blob && slide.type === "image") {
      if (slide.loaded) {
        super.setContent(slide)
        return
      }

      slide = await setBlobContent(slide)
    }
    if (!slide.$el) return
    super.setContent(slide)
  }
}

class CustomHtml extends Plugins.Html {
  async setContent(slide) {
    if (slide?.blob && slide.type !== "image") {
      if (slide.loaded) {
        super.setContent(slide)
        return
      }

      slide = await setBlobContent(slide)
    }
    super.setContent(slide)

    if (slide.type === "html5video") {
      attachSlideStreamRecovery(slide)
    }
  }
}

Fancybox.Plugins = { ...Plugins, Image: CustomImage, Html: CustomHtml }

Fancybox.defaults.Image = zoomImageOptions
Fancybox.defaults.Toolbar = zoomToolbarOptions

export const $fancybox = Fancybox
